import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apis from '../../apis';

import useTicketEventData from '../../hooks/useTicketEventData';
import CustomSeatPicker from '../SelectSeat/CustomSeatPicker';
import './PublicTicketBookingPage.css';

const formatRupiah = (n) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

const STEPS_BASE = ['Select Winner', 'Select Paid Seats & Add-ons', 'Your Details', 'Review & Pay'];

export default function PublicTicketBookingPage() {

  const navigate = useNavigate();
  const { event, loading: eventLoading, error: eventError } = useTicketEventData();

  const dynamicVenueInfo = useMemo(() => {
    const info = {};
    (event?.venues || []).forEach(v => {
      info[v.id] = { label: v.label, img: v.imageUrl };
    });
    return info;
  }, [event]);

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const topRef = useRef(null);

  // Step 0 - Winner Selection
  const [winners, setWinners] = useState([]);
  const [winnersLoading, setWinnersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWinner, setSelectedWinner] = useState(null);

  const [buyerType, setBuyerType] = useState('public'); // 'public' or 'winner'
  const [selectedSessionId, setSelectedSessionId] = useState('');

  const [venue, setVenue] = useState('');
  const [date, setDate] = useState('');
  const [session, setSession] = useState('');

  const [eligibility, setEligibility] = useState(null);

  const isPublicEnabled = useMemo(() => {
    if (!eligibility || !eligibility.enabled) return true;
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' });
    const todayEntries = (eligibility.schedule || []).filter(s => s.date === today);
    if (todayEntries.length === 0) return false;
    return todayEntries.some(s => s.allowedTiers?.includes('Public'));
  }, [eligibility]);

  useEffect(() => {
    if (!isPublicEnabled && buyerType === 'public') {
      setBuyerType('winner');
    }
  }, [isPublicEnabled, buyerType]);

  const allSessions = useMemo(() => {
    const sessionsList = [];

    // Add orchestra sessions
    (event?.orchestraSessions || []).forEach(os => {
      sessionsList.push({
        ...os,
        isOrchestra: true,
        label: `[Orchestra] ${dynamicVenueInfo[os.venue]?.label || os.venue} | ${os.date} | ${os.time}`
      });
    });

    // Add masterclass sessions
    (event?.masterclassSessions || []).forEach(ms => {
      sessionsList.push({
        ...ms,
        isMasterclass: true,
        label: `[Masterclass] ${dynamicVenueInfo[ms.venue]?.label || ms.venue} | ${ms.date} | ${ms.time}`
      });
    });

    // Add normal competition sessions
    (event?.venues || []).forEach(v => {
      if (v.sessions) {
        Object.entries(v.sessions).forEach(([d, times]) => {
          (times || []).forEach(t => {
            const sId = `${v.id}_${d}_${t}`;
            // check if this is already in orchestra sessions to avoid duplicate?
            // orchestra sessions have id format string, let's just add it
            sessionsList.push({
              id: sId,
              venue: v.id,
              date: d,
              time: t,
              isOrchestra: false,
              complimentaryQuota: 0,
              complimentaryClaimed: 0,
              reservedRows: [],
              label: `${v.label || v.id} | ${d} | ${t}`
            });
          });
        });
      }
    });

    return sessionsList;
  }, [event, dynamicVenueInfo]);

  const selectedSessionObj = useMemo(() => allSessions.find(s => s.id === selectedSessionId), [allSessions, selectedSessionId]);

  useEffect(() => {
    if (buyerType === 'public' && selectedSessionObj) {
      setVenue(selectedSessionObj.venue);
      setDate(selectedSessionObj.date);
      setSession(selectedSessionObj.time);
    }
  }, [buyerType, selectedSessionObj]);

  useEffect(() => {
    if (buyerType === 'winner' && selectedWinner?.session) {
      setVenue(selectedWinner.session.venue);
      setDate(selectedWinner.session.date);
      setSession(selectedWinner.session.time);
    }
  }, [buyerType, selectedWinner]);

  // Step 1 – seat picking & add-ons
  const [seatLayout, setSeatLayout] = useState([]);
  const [seatLayoutLoading, setSeatLayoutLoading] = useState(false);
  const [selections, setSelections] = useState({});   // { tierId: [seatObj] }
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [quantities, setQuantities] = useState({});

  const totalTicketsPurchased = useMemo(() =>
    Object.values(quantities).reduce((a, b) => a + (b || 0), 0),
    [quantities]);
  const hasSeatSelectionAddon = selectedAddOns.filter(id => id === 'seat_selection_performer').length > 0;

  // Total seats selected
  const totalSelected = useMemo(() =>
    Object.values(selections).reduce((s, arr) => s + arr.length, 0),
    [selections]
  );

  const { P: paidTicketCount, F: complimentaryCount } = useMemo(() => {
    if (buyerType !== 'winner' || !selectedSessionObj) return { P: 0, F: 0 };

    // The quota for free tickets is defined on the selected ORCHESTRA session
    const quota = selectedSessionObj.complimentaryQuota || 0;
    const claimed = selectedSessionObj.complimentaryClaimed || 0;
    const quotaLeft = Math.max(0, quota - claimed);

    // P = number of seats they clicked on the performance map
    const P = totalSelected;
    // F = number of tickets purchased + 1 (for themselves), capped by remaining quota
    const F = Math.min(totalTicketsPurchased + 1, quotaLeft);
    return { P, F };
  }, [buyerType, selectedSessionObj, totalSelected, totalTicketsPurchased]);

  const complimentaryGranted = complimentaryCount > 0;

  const hasOrchestraStep = buyerType === 'winner' && complimentaryCount > 0;

  const STEPS = useMemo(() => {
    return hasOrchestraStep
      ? ['Select Winner', 'Select Paid Seats & Add-ons', 'Select Free Orchestra Seats', 'Your Details', 'Review & Pay']
      : STEPS_BASE;
  }, [hasOrchestraStep]);

  // Step 2 - Orchestra Seats (Dynamic)
  const [orchestraSeatLayout, setOrchestraSeatLayout] = useState([]);
  const [orchestraSeatLoading, setOrchestraSeatLoading] = useState(false);
  const [orchestraSelections, setOrchestraSelections] = useState({});

  const totalOrchestraSelected = useMemo(() =>
    Object.values(orchestraSelections).reduce((s, arr) => s + arr.length, 0),
    [orchestraSelections]
  );

  const allOrchestraSelectedSeatIds = useMemo(() =>
    Object.values(orchestraSelections).flat().map(s => s.id),
    [orchestraSelections]
  );

  // Step 2
  const [registrantName, setRegistrantName] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: 'smooth' });

  // Fetch eligible winners on mount
  useEffect(() => {
    const fetchWinners = async () => {
      setWinnersLoading(true);
      try {
        const res = await apis.publicTicket.getEligibleWinners();
        console.log("Eligible winners: ", res);
        if (res?.error) throw new Error('Failed to load winners.');
        setWinners(res.data?.winners || []);
      } catch (err) {
        console.error('Fetch winners error', err);
      } finally {
        setWinnersLoading(false);
      }
    };
    fetchWinners();
  }, []);

  useEffect(() => {
    const fetchEligibility = async () => {
      try {
        const res = await apis.systemSettings.getGlobalSettings();
        if (res.data?.ticketEligibility) {
          setEligibility(res.data.ticketEligibility);
        }
      } catch (err) {
        console.error('Fetch eligibility error', err);
      }
    };
    fetchEligibility();
  }, []);

  // Fetch live seat layout when entering step 1
  useEffect(() => {
    if (step !== 1 || !venue || !date || !session) return;
    const fetchSeats = async () => {
      setSeatLayoutLoading(true);
      try {
        const sessionId = `${date}_${session}`;
        const res = await apis.publicTicket.getSeats(venue, sessionId);
        console.log("res seats", res)
        if (res?.error) throw new Error('Failed to load seats.');
        setSeatLayout(res.data || []);
      } catch (e) {
        console.error('Seat fetch error', e);
      } finally {
        setSeatLayoutLoading(false);
      }
    };
    fetchSeats();
  }, [step, venue, date, session]);

  // Fetch live seat layout for Orchestra when entering step 2 (if applicable)
  useEffect(() => {
    if (step !== 2 || !hasOrchestraStep || !selectedSessionId) return;
    const fetchOrchestraSeats = async () => {
      setOrchestraSeatLoading(true);
      try {
        const os = allSessions.find(s => s.id === selectedSessionId);
        if (!os) return;
        const oSessionId = `${os.date}_${os.time}`;
        const res = await apis.publicTicket.getSeats(os.venue, oSessionId);
        if (res?.error) throw new Error('Failed to load orchestra seats.');
        setOrchestraSeatLayout(res.data || []);
      } catch (e) {
        console.error('Orchestra seat fetch error', e);
      } finally {
        setOrchestraSeatLoading(false);
      }
    };
    fetchOrchestraSeats();
  }, [step, hasOrchestraStep, selectedSessionId, allSessions]);

  const tierColors = useMemo(() => {
    if (!event?.ticketTiers) {
      // Fallback
      return {
        'presto': '#EBBC64',
        'allegro': '#C0C0C0',
        'lento': '#CD7F32',
      };
    }
    return event.ticketTiers.reduce((acc, t) => {
      acc[t.id.toLowerCase()] = t.color || '#888888';
      return acc;
    }, {});
  }, [event?.ticketTiers]);

  const rightSideMax = useMemo(() => ({
    A: 9, B: 10, C: 10, D: 9, E: 9, F: 11,
    G: 9, H: 9, I: 9, J: 8, K: 8, L: 9, M: 10,
    N: 13, O: 13, P: 13, Q: 13, R: 12, S: 12, T: 12, U: 12
  }), []);

  // Format seat layout as a single unified theater map
  const unifiedLayout = useMemo(() => {
    if (!seatLayout.length) return [];

    // Group all seats by row first, ignoring tier
    const byRow = seatLayout.reduce((acc, s) => {
      if (!acc[s.row]) acc[s.row] = [];
      acc[s.row].push(s);
      return acc;
    }, {});

    // Sort rows alphabetically (A, B, C...)
    const sortedRowKeys = Object.keys(byRow).sort((a, b) => a.localeCompare(b));

    return sortedRowKeys.map(row => {
      // Sort seats within row by number descending (right to left)
      byRow[row].sort((a, b) => b.number - a.number);

      const mappedRow = byRow[row].map(s => {
        const isReservedRow = selectedSessionObj?.reservedRows?.some(r => r.toUpperCase() === s.row.toUpperCase());
        const normalizedAreaType = s.areaType?.toLowerCase();
        return {
          id: s.id, number: s.number, row: s.row, areaType: s.areaType, seatLabel: s.seatLabel,
          isReserved: s.status !== 'available' || (isReservedRow && !hasSeatSelectionAddon),
          color: (isReservedRow && !hasSeatSelectionAddon) ? '#444444' : (tierColors[normalizedAreaType] || '#888888'),
          tooltip: (isReservedRow && !hasSeatSelectionAddon) ? `${s.seatLabel} – Reserved for Registrants` : `${s.seatLabel} – ${s.status !== 'available' ? 'Taken' : 'Available'}`,
        };
      });

      if (selectedSessionObj?.venue === 'Venue1') {
        const splitSeatNumber = rightSideMax[row];
        if (splitSeatNumber) {
          const splitIndex = mappedRow.findIndex(s => s.number === splitSeatNumber);
          if (splitIndex !== -1) {
            // Insert two nulls for a wide aisle
            mappedRow.splice(splitIndex, 0, null, null);
          }
        }
      }

      return mappedRow;
    }).filter(r => r.length > 0);
  }, [seatLayout, tierColors, rightSideMax, selectedSessionObj, hasSeatSelectionAddon]);

  const unifiedOrchestraLayout = useMemo(() => {
    if (!orchestraSeatLayout.length) return [];

    const byRow = orchestraSeatLayout.reduce((acc, s) => {
      if (!acc[s.row]) acc[s.row] = [];
      acc[s.row].push(s);
      return acc;
    }, {});

    const sortedRowKeys = Object.keys(byRow).sort((a, b) => a.localeCompare(b));

    return sortedRowKeys.map(row => {
      byRow[row].sort((a, b) => b.number - a.number);

      const mappedRow = byRow[row].map(s => {
        const normalizedAreaType = s.areaType?.toLowerCase();
        return {
          id: s.id, number: s.number, row: s.row, areaType: s.areaType, seatLabel: s.seatLabel,
          isReserved: s.status !== 'available',
          color: tierColors[normalizedAreaType] || '#888888',
          tooltip: `${s.seatLabel} – ${s.status !== 'available' ? 'Taken' : 'Available'}`,
        };
      });

      if (selectedSessionObj?.venue === 'Venue1') {
        const splitSeatNumber = rightSideMax[row];
        if (splitSeatNumber) {
          const splitIndex = mappedRow.findIndex(s => s.number === splitSeatNumber);
          if (splitIndex !== -1) {
            mappedRow.splice(splitIndex, 0, null, null);
          }
        }
      }

      return mappedRow;
    }).filter(r => r.length > 0);
  }, [orchestraSeatLayout, tierColors, rightSideMax, selectedSessionObj]);

  // All selected seat IDs (flat)
  const allSelectedSeatIds = useMemo(() =>
    Object.values(selections).flat().map(s => s.id),
    [selections]
  );

  // Helper to get venue-specific price for a tier
  const getVenuePrice = (tier) => {
    if (!venue) return null;
    return tier.venuePrices?.[venue] ?? null;
  };

  // Check if all tiers have pricing configured for the selected venue
  const hasMissingPricing = useMemo(() => {
    if (!venue || !event?.ticketTiers) return false;
    return event.ticketTiers.some(t => {
      const price = t.venuePrices?.[venue];
      return price === undefined || price === null;
    });
  }, [event, venue]);

  const seatSelectionPerformerCount = useMemo(() =>
    selectedAddOns.filter(id => id === 'seat_selection_performer').length,
    [selectedAddOns]);

  const freeMasterclassCount = useMemo(() => quantities['presto'] || 0, [quantities]);

  const orderTotal = useMemo(() => {
    let total = 0;
    (event?.ticketTiers || []).forEach(t => {
      const normalizedId = t.id.toLowerCase();
      const venuePrice = getVenuePrice(t);
      if (venuePrice !== null) {
        total += (quantities[normalizedId] || 0) * Number(venuePrice);
      }
    });
    (event?.addOns || []).forEach(a => {
      const qty = selectedAddOns.filter(id => id === a.id).length;
      if (qty > 0) {
        total += Number(a.price) * qty;
      }
    });
    return total;
  }, [event, quantities, selectedAddOns, venue]);

  // Ensure seat_selection is unselected if complimentaryCount === 0
  useEffect(() => {
    if (buyerType === 'winner' && complimentaryCount === 0 && selectedAddOns.includes('seat_selection')) {
      setSelectedAddOns(prev => prev.filter(a => a !== 'seat_selection'));
    }
  }, [complimentaryCount, buyerType, selectedAddOns]);

  // Build tickets array for submission
  const ticketsPayload = useMemo(() => {
    const qtyCopy = { ...quantities };

    return (event?.ticketTiers || [])
      .filter(t => (qtyCopy[t.id.toLowerCase()] || 0) > 0)
      .map(t => {
        const venuePrice = getVenuePrice(t);
        return { id: t.id, name: t.name, quantity: qtyCopy[t.id.toLowerCase()], priceEach: venuePrice ?? 0 };
      });
  }, [event, quantities, venue]);

  const canProceedStep0 = buyerType === 'public'
    ? !!selectedSessionId
    : (!!selectedWinner && !!selectedSessionId);
  const seatDataMissing = !selectedSessionObj?.isMasterclass && !seatLayoutLoading && step === 1 && seatLayout.length === 0;
  const canProceedStep1 = selectedSessionObj?.isMasterclass
    ? totalTicketsPurchased > 0
    : (!hasMissingPricing && (!seatDataMissing || seatSelectionPerformerCount === 0) && (totalSelected === seatSelectionPerformerCount) && totalTicketsPurchased > 0);
  const isOrchestraSeatSelectionChecked = selectedAddOns.includes('seat_selection');
  const canProceedStepOrchestra = isOrchestraSeatSelectionChecked ? (totalOrchestraSelected === complimentaryCount) : true;

  const logicalStepDetails = hasOrchestraStep ? 3 : 2;
  const canProceedStepDetails = buyerName.trim() && (buyerType === 'winner' ? registrantName.trim() : true) && userEmail.trim() && userPhone.trim();

  const goNext = () => { setStep(s => s + 1); scrollTop(); };
  const goBack = () => { setStep(s => s - 1); scrollTop(); };

  const handleSelectWinner = (winner) => {
    setSelectedWinner(winner);
    setRegistrantName(winner.name || '');
    setBuyerName(winner.name || '');
    setUserEmail(winner.email || '');
  };

  const filteredWinners = useMemo(() => {
    if (!searchTerm) return winners;
    const lower = searchTerm.toLowerCase();
    return winners.filter(w =>
      w.name?.toLowerCase().includes(lower) ||
      w.teacherName?.toLowerCase().includes(lower)
    );
  }, [winners, searchTerm]);

  const handleSelectSeat = (seat) => {
    const tierId = seat.areaType?.toLowerCase();
    const currentSelectedInTier = (selections[tierId] || []).length;
    if (currentSelectedInTier >= (quantities[tierId] || 0)) {
      Modal.warning({ title: 'Limit Exceeded', content: `You cannot select more ${seat.areaType} seats than you purchased tickets for.` });
      return;
    }
    if (totalSelected >= seatSelectionPerformerCount) {
      Modal.warning({ title: 'Limit Exceeded', content: `You can only select up to ${seatSelectionPerformerCount} seats based on your Add-on quantity.` });
      return;
    }
    setSelections(p => ({ ...p, [tierId]: [...(p[tierId] || []), seat] }));
  };
  const handleDeselectSeat = (seat) => {
    const tierId = seat.areaType?.toLowerCase();
    setSelections(p => ({ ...p, [tierId]: (p[tierId] || []).filter(s => s.id !== seat.id) }));
  };

  const handleSelectOrchestraSeat = (seat) => {
    if (totalOrchestraSelected >= complimentaryCount) return;
    const tierId = seat.areaType?.toLowerCase();
    setOrchestraSelections(p => ({ ...p, [tierId]: [...(p[tierId] || []), seat] }));
  };
  const handleDeselectOrchestraSeat = (seat) => {
    const tierId = seat.areaType?.toLowerCase();
    setOrchestraSelections(p => ({ ...p, [tierId]: (p[tierId] || []).filter(s => s.id !== seat.id) }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const payload = {
        registrantName, buyerName, userEmail, userPhone,
        venue, date, session,
        orchestraSessionId: selectedSessionId,
        isOrchestra: !!selectedSessionObj?.isOrchestra,
        isMasterclass: !!selectedSessionObj?.isMasterclass,
        isPublic: buyerType === 'public',
        complimentaryTickets: complimentaryCount,
        tickets: ticketsPayload,
        selectedSeatIds: allSelectedSeatIds,
        orchestraSelectedSeatIds: hasOrchestraStep ? allOrchestraSelectedSeatIds : [],
        performanceSeatLabels: Object.values(selections).flat().map(s => s.seatLabel),
        orchestraSeatLabels: hasOrchestraStep ? Object.values(orchestraSelections).flat().map(s => s.seatLabel) : [],
        addOnIds: selectedAddOns,
        freeMasterclassCount,
        registrantId: selectedWinner?.registrantId,
      };
      const res = await apis.publicTicket.createBooking(payload);
      if (res?.error) throw new Error(res.error?.data?.message || 'Booking failed.');
      const { bookingId, paymentUrl } = res.data;
      window.open(paymentUrl, '_blank');
      navigate(`/waiting-payment/${bookingId}`, { state: { paymentLink: paymentUrl, isPublicTicket: true } });
    } catch (err) {
      setSubmitError(err.response?.data?.message || err.message || 'Booking failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (eventLoading) return (
    <div className="ptb-loading">
      <div className="ptb-spinner" />
      <p>Loading event details…</p>
    </div>
  );

  if (eventError) return (
    <div className="ptb-error">
      <p>Unable to load event data. Please refresh the page.</p>
    </div>
  );


  const renderCartSummaryInner = () => (
    <>
      <h3>Order Summary</h3>
      {totalTicketsPurchased === 0 ? (
        <div className="ptb-empty-cart">No tickets selected yet.</div>
      ) : (
        <div className="ptb-cart-items">
          <>
            {(event?.ticketTiers || []).map(tier => {
              const normalizedId = tier.id.toLowerCase();
              const qty = quantities[normalizedId] || 0;
              if (qty === 0) return null;
              const venuePrice = getVenuePrice(tier);
              return (
                <div key={tier.id} className="ptb-cart-row">
                  <span>{qty}x {tier.name}</span>
                  <span>{venuePrice !== null ? formatRupiah(qty * venuePrice) : 'N/A'}</span>
                </div>
              );
            })}
          </>
          {(() => {
            const uniqueAddOnIds = [...new Set(selectedAddOns)];
            return uniqueAddOnIds.map(addonId => {
              const a = event?.addOns?.find(x => x.id === addonId);
              if (!a) return null;
              const qty = selectedAddOns.filter(id => id === addonId).length;
              return (
                <div key={a.id} className="ptb-cart-row ptb-cart-addon">
                  <span>{qty}x {a.name}</span>
                  <span>{formatRupiah(a.price * qty)}</span>
                </div>
              );
            });
          })()}
          {freeMasterclassCount > 0 && (
            <div className="ptb-cart-row ptb-cart-addon" style={{ color: '#EBBC64' }}>
              <span>{freeMasterclassCount}x Master Class (Free with Presto)</span>
              <span>Free</span>
            </div>
          )}
          <div className="ptb-cart-total">
            <span>Total</span>
            <span>{formatRupiah(orderTotal)}</span>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="ptb-root" ref={topRef}>
      {/* ── Google Fonts ── */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500&display=swap" rel="stylesheet" />

      {/* ── Header ── */}
      <header className="ptb-header">
        <div className="ptb-header-inner">
          <span className="ptb-brand">APCS <span className="ptb-brand-accent">2026</span></span>
          <span className="ptb-subtitle">Gala Concert Tickets</span>
        </div>
      </header>

      {/* ── Progress ── */}
      <div className="ptb-stepper">
        {STEPS.map((label, i) => (
          <div key={i} className={`ptb-step ${i === step ? 'active' : i < step ? 'done' : ''}`}>
            <div className="ptb-step-circle">{i < step ? '✓' : i + 1}</div>
            <span className="ptb-step-label">{label}</span>
            {i < STEPS.length - 1 && <div className="ptb-step-line" />}
          </div>
        ))}
      </div>

      {/* ── Content ── */}
      <main className="ptb-main">

        {/* STEP 0 — Select Winner */}
        {step === 0 && (
          <div className="ptb-card ptb-animate">
            <h2 className="ptb-card-title">Identify Your Entry</h2>
            <p className="ptb-card-subtitle">Please select the registered winner to proceed with ticket booking.</p>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
              {isPublicEnabled && (
                <button
                  className={`ptb-btn-ghost ${buyerType === 'public' ? 'active-tab' : ''}`}
                  onClick={() => setBuyerType('public')}
                  style={{ flex: 1, backgroundColor: buyerType === 'public' ? '#EBBC64' : 'transparent', color: buyerType === 'public' ? '#000' : '#EBBC64' }}
                >
                  Public Buyer
                </button>
              )}
              <button
                className={`ptb-btn-ghost ${buyerType === 'winner' ? 'active-tab' : ''}`}
                onClick={() => setBuyerType('winner')}
                style={{ flex: 1, backgroundColor: buyerType === 'winner' ? '#EBBC64' : 'transparent', color: buyerType === 'winner' ? '#000' : '#EBBC64' }}
              >
                Registered Winner
              </button>
            </div>

            {buyerType === 'winner' && (
              <>
                <div className="ptb-search-box">
                  <SearchOutlined className="ptb-search-icon" />
                  <input
                    type="text"
                    placeholder="Search by your name or teacher..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="ptb-search-input"
                  />
                </div>

                {winnersLoading ? (
                  <div className="ptb-winner-loading">
                    <div className="ptb-spinner-sm" />
                    <p>Loading eligible winners...</p>
                  </div>
                ) : (
                  <div className="ptb-winner-grid">
                    {filteredWinners.length > 0 ? (
                      filteredWinners.map(w => (
                        <div
                          key={w.registrantId}
                          className={`ptb-winner-card ${selectedWinner?.registrantId === w.registrantId ? 'selected' : ''}`}
                          style={{ marginTop: 16 }}
                          onClick={() => handleSelectWinner(w)}
                        >
                          <div className="ptb-winner-info">
                            <div className="ptb-winner-header">
                              <span className={`ptb-award-tag ${w.finalAward.toLowerCase()}`}>
                                {w.finalAward}
                              </span>
                              <span className="ptb-winner-category">{w.competitionCategory}</span>
                            </div>
                            <h3 className="ptb-winner-name">{w.name}</h3>
                            <p className="ptb-winner-teacher">Teacher: {w.teacherName}</p>
                          </div>
                          <div className="ptb-winner-assignment">
                            <div className="ptb-assignment-row">
                              <span className="ptb-label">Venue:</span>
                              <span className="ptb-value">{dynamicVenueInfo[w.session?.venue]?.label || w.session?.venue}</span>
                            </div>
                            <div className="ptb-assignment-row">
                              <span className="ptb-label">Session:</span>
                              <span className="ptb-value">{w.session?.date} | {w.session?.time}</span>
                            </div>
                          </div>
                          {selectedWinner?.registrantId === w.registrantId && (
                            <div className="ptb-selected-check">✓ Selected</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="ptb-no-results">
                        <UserOutlined style={{ fontSize: '2rem', color: '#444', marginBottom: '1rem' }} />
                        <p>No winners found matching your search.</p>
                        <p className="ptb-help-text">Can't find your name? Please contact our admin for assistance.</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ color: '#fff', marginBottom: '1rem' }}>
                {buyerType === 'winner' ? 'Select Orchestra Session' : 'Select Session'}
              </h3>
              <select
                className="ptb-input"
                value={selectedSessionId}
                onChange={(e) => setSelectedSessionId(e.target.value)}
              >
                <option value="">-- Choose a Session --</option>
                {allSessions
                  .filter(os => buyerType === 'public' || os.isOrchestra)
                  .map(os => (
                    <option key={os.id} value={os.id}>
                      {os.label}
                    </option>
                  ))}
              </select>
            </div>

            <div className="ptb-actions" style={{ marginTop: '2rem' }}>
              <button className="ptb-btn-primary" disabled={!selectedSessionId || (buyerType === 'winner' && !selectedWinner)} onClick={() => setStep(1)}>
                Next: Select Tickets &rarr;
              </button>
            </div>
          </div>
        )}

        {/* STEP 1 — Seat Selection & Add-ons */}
        {step === 1 && (
          <div className="ptb-card ptb-animate ptb-step-fused">
            <h2 className="ptb-card-title">Select Seats & Add-ons</h2>


            <div className="ptb-fused-layout">
              <div className="ptb-fused-main">

                {/* 1. Ticket Quantities */}
                <div className="ptb-tier-section ptb-tier-section-fused" style={{ marginBottom: '2rem' }}>
                  <div className="ptb-tier-header">
                    <h3 className="ptb-section-label">Select Tickets</h3>
                  </div>
                  <div className="ptb-ticket-quantities" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {(event?.ticketTiers || [])
                      .filter(t => selectedSessionObj?.isMasterclass ? t.id.toLowerCase() === 'masterclass' : t.id.toLowerCase() !== 'masterclass')
                      .map(t => {
                        const tierId = t.id.toLowerCase();
                        const venuePrice = getVenuePrice(t);
                        const qty = quantities[tierId] || 0;
                        return (
                          <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#2d3748', padding: '1rem', borderRadius: '8px' }}>
                            <div>
                              <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>{t.name}</span>
                              <span style={{ display: 'block', color: '#a0aec0', fontSize: '0.9rem' }}>{venuePrice !== null ? formatRupiah(venuePrice) : 'Price N/A'}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', background: '#1a202c', padding: '4px 8px', borderRadius: '4px' }}>
                              <button className="ptb-btn-ghost" style={{ border: 'none', fontSize: '1.5rem', lineHeight: 1, padding: '0 8px' }} onClick={() => {
                                const currentSelected = (selections[tierId] || []).length;
                                if (!selectedSessionObj?.isMasterclass && qty - 1 < currentSelected) {
                                  Modal.warning({ title: 'Cannot Reduce Quantity', content: `Please deselect ${t.name} seats on the map below first before reducing quantity.` });
                                  return;
                                }
                                setQuantities(prev => ({ ...prev, [tierId]: Math.max(0, qty - 1) }));
                              }}>-</button>
                              <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: 'bold', fontSize: '1.2rem' }}>{qty}</span>
                              <button className="ptb-btn-ghost" style={{ border: 'none', fontSize: '1.5rem', lineHeight: 1, padding: '0 8px' }} onClick={() => {
                                setQuantities(prev => ({ ...prev, [tierId]: qty + 1 }));
                              }}>+</button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* 2. Add-ons */}
                {!selectedSessionObj?.isMasterclass && (event?.addOns || []).length > 0 && (
                  <div className="ptb-addons-section" style={{ marginBottom: '2rem' }}>
                    <h3 className="ptb-section-label">Add-ons (Optional)</h3>
                    <div className="ptb-addon-list">
                      {event.addOns.filter(a => a.id !== 'seat_selection').map(a => {
                        const qty = selectedAddOns.filter(id => id === a.id).length;

                        if (a.id === 'allegro_masterclass' || a.id === 'seat_selection_performer') {
                          return (
                            <div key={a.id} className={`ptb-addon-item ${qty > 0 ? 'selected' : ''}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <span>{a.name}</span>
                                <span className="ptb-addon-price" style={{ marginLeft: '8px' }}>
                                  +{formatRupiah(a.price)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#2d3748', padding: '4px 8px', borderRadius: '4px' }}>
                                <button className="ptb-btn-ghost" style={{ padding: '2px 8px', minWidth: 'auto', border: 'none', fontSize: '1.2rem', lineHeight: 1 }} onClick={() => {
                                  if (qty > 0) {
                                    if (a.id === 'seat_selection_performer' && totalSelected > qty - 1) {
                                      Modal.warning({ title: 'Cannot Reduce Quantity', content: `Please deselect seats on the map first before reducing seat selection quantity.` });
                                      return;
                                    }
                                    const index = selectedAddOns.lastIndexOf(a.id);
                                    const newArr = [...selectedAddOns];
                                    newArr.splice(index, 1);
                                    setSelectedAddOns(newArr);
                                  }
                                }}>-</button>
                                <span style={{ minWidth: '20px', textAlign: 'center', fontWeight: 'bold' }}>{qty}</span>
                                <button className="ptb-btn-ghost" style={{ padding: '2px 8px', minWidth: 'auto', border: 'none', fontSize: '1.2rem', lineHeight: 1 }} onClick={() => {
                                  if (a.id === 'seat_selection_performer' && qty >= totalTicketsPurchased) {
                                    Modal.warning({ title: 'Limit Exceeded', content: `You cannot select more seats than the total tickets you are purchasing (${totalTicketsPurchased}).` });
                                    return;
                                  }
                                  setSelectedAddOns([...selectedAddOns, a.id]);
                                }}>+</button>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <label key={a.id} className={`ptb-addon-item ${qty > 0 ? 'selected' : ''}`} style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={qty > 0}
                              onChange={() => setSelectedAddOns(p => p.includes(a.id) ? p.filter(x => x !== a.id) : [...p, a.id])}
                            />
                            <span>{a.name}</span>
                            <span className="ptb-addon-price">+{formatRupiah(a.price)}</span>
                          </label>
                        )
                      })}
                    </div>
                    {freeMasterclassCount > 0 && (
                      <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', backgroundColor: '#2d3748', borderRadius: '8px', border: '1px solid #EBBC64', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '1.5rem' }}>🎁</span>
                        <p style={{ margin: 0, color: '#e2e8f0', fontSize: '0.95rem' }}>
                          You receive <strong>{freeMasterclassCount} complimentary Masterclass pass{freeMasterclassCount > 1 ? 'es' : ''}</strong> automatically with your Presto tickets!
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* 3. Seat Map (Only visible if applicable) */}
                {!selectedSessionObj?.isMasterclass && seatSelectionPerformerCount > 0 && (
                  <>
                    {seatLayoutLoading ? (
                      <div className="ptb-seat-loading"><div className="ptb-spinner" /><p>Loading seat map…</p></div>
                    ) : (
                      <div className="ptb-tier-section ptb-tier-section-fused">
                        <div className="ptb-tier-header">
                          <h3 className="ptb-section-label">Venue Map: {dynamicVenueInfo[venue]?.label}</h3>
                        </div>
                        {dynamicVenueInfo[venue]?.img && (
                          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <img src={dynamicVenueInfo[venue].img} alt="Venue Map" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #444' }} />
                          </div>
                        )}
                        {seatDataMissing ? (
                          <div style={{ padding: '1.5rem', backgroundColor: '#742a2a', borderRadius: '8px', border: '1px solid #fc8181', textAlign: 'center' }}>
                            <p style={{ margin: 0, color: '#fed7d7', fontWeight: 600, fontSize: '1.1rem' }}>⚠️ No seats found for this session</p>
                            <p style={{ margin: '0.5rem 0 0 0', color: '#feb2b2', fontSize: '0.9rem' }}>
                              The seat data for <strong>{dynamicVenueInfo[venue]?.label || venue}</strong> — <strong>{date} | {session}</strong> could not be loaded.
                              Please contact the admin to generate seats for this session.
                            </p>
                          </div>
                        ) : (
                          <div className="ptb-seat-scroll">
                            <CustomSeatPicker
                              layout={unifiedLayout}
                              selectedSeats={Object.values(selections).flat()}
                              onSelect={handleSelectSeat}
                              onDeselect={handleDeselectSeat}
                              maxSeats={Infinity}
                              onSeatClick={null}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div className="ptb-seat-legend">
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', width: '100%', marginBottom: '1rem', borderBottom: '1px solid #1f1f1f', paddingBottom: '1rem' }}>
                        {(event?.ticketTiers || []).map(t => {
                          const venuePrice = getVenuePrice(t);
                          return (
                            <span key={t.id} style={{ display: 'flex', alignItems: 'center', color: '#f0f0f0' }}>
                              <span className="ptb-legend-dot" style={{ backgroundColor: tierColors[t.id] || '#888', marginRight: '6px' }} />
                              {t.name} ({venuePrice !== null ? formatRupiah(venuePrice) : 'Price N/A'})
                            </span>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot available" /> Available</span>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot selected" /> Selected</span>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot taken" /> Taken</span>
                      </div>
                    </div>

                  </>
                )}
              </div>

              <div className="ptb-fused-sidebar">
                <div className="ptb-cart-summary">
                  {renderCartSummaryInner()}
                </div>
              </div>
            </div>

            {buyerType === 'winner' && (
              <div style={{ padding: '1rem', backgroundColor: complimentaryGranted ? '#2d3748' : '#742a2a', borderRadius: '8px', marginBottom: '1rem', border: `1px solid ${complimentaryGranted ? '#EBBC64' : '#fc8181'}` }}>
                {complimentaryGranted ? (
                  <>
                    <h3 style={{ color: '#EBBC64', margin: 0 }}>{complimentaryCount} Complimentary Tickets Granted!</h3>
                    <p style={{ margin: '0.5rem 0 0 0', color: '#e2e8f0' }}>
                      Based on your {paidTicketCount} paid ticket(s) + 1 for yourself, you receive {complimentaryCount} free ticket(s).
                      You will have the option to manually pick these free seats in the next step!
                    </p>
                    {paidTicketCount + 1 > complimentaryCount && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#fed7d7', color: '#822727', borderRadius: '4px', fontSize: '0.9rem', border: '1px solid #fc8181' }}>
                        <strong>Notice:</strong> You are eligible for {paidTicketCount + 1} complimentary tickets, but the session only has {complimentaryCount} free ticket(s) remaining in its quota. Your grant has been capped.
                      </div>
                    )}
                  </>
                ) : (
                  <p style={{ margin: 0, color: '#fed7d7' }}>
                    Notice: Complimentary tickets for this session have been fully claimed, or you haven't picked seats yet. Add seats on the map to see your free ticket quota!
                  </p>
                )}
              </div>
            )}

            {hasMissingPricing && (
              <div style={{ padding: '1rem', backgroundColor: '#742a2a', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #fc8181' }}>
                <p style={{ margin: 0, color: '#fed7d7' }}>
                  <strong>⚠️ Ticket pricing has not been configured for this venue yet.</strong> Please contact the event administrator. Checkout is disabled until pricing is set.
                </p>
              </div>
            )}

            <div className="ptb-actions">
              <button className="ptb-btn-ghost" onClick={goBack}>← Back</button>
              <button className="ptb-btn-primary" disabled={!canProceedStep1} onClick={goNext}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* STEP Orchestra — Free Seats */}
        {step === 2 && hasOrchestraStep && (
          <div className="ptb-card ptb-animate ptb-step-two">
            <h2 className="ptb-card-title">Select Free Orchestra Seats</h2>

            <div className="ptb-two-column">
              <div className="ptb-left-col">
                <p className="ptb-card-subtitle">
                  Pick your {complimentaryCount} complimentary seats for the Orchestra Gala Concert.
                </p>

                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#2d3748', borderRadius: '8px', border: '1px solid #4a5568' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={isOrchestraSeatSelectionChecked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAddOns(prev => [...prev, 'seat_selection']);
                        } else {
                          setSelectedAddOns(prev => prev.filter(a => a !== 'seat_selection'));
                          setOrchestraSelections({}); // Clear selections if they uncheck
                        }
                      }}
                      style={{ marginRight: '10px', width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>Select Seats on Map (Add-on)</span>
                  </label>
                  <p style={{ margin: '0.5rem 0 0 30px', color: '#a0aec0', fontSize: '0.9rem' }}>
                    Optional: Pay a flat fee to manually pick your {complimentaryCount} complimentary seats. If you skip this, your seats will be assigned on-the-spot automatically.
                  </p>
                </div>

                {isOrchestraSeatSelectionChecked && (
                  <>
                    <div className="ptb-map-container" style={{ position: 'relative' }}>
                      <div className="ptb-stage">ORCHESTRA STAGE</div>

                      {orchestraSeatLoading ? (
                        <div className="ptb-map-loading">
                          <div className="ptb-spinner" />
                          <p>Loading orchestra seat map...</p>
                        </div>
                      ) : (
                        <div className="ptb-seat-scroll">
                          <CustomSeatPicker
                            layout={unifiedOrchestraLayout}
                            selectedSeats={Object.values(orchestraSelections).flat()}
                            onSelect={handleSelectOrchestraSeat}
                            onDeselect={handleDeselectOrchestraSeat}
                            maxSeats={complimentaryCount}
                          />
                        </div>
                      )}
                    </div>

                    <div className="ptb-seat-legend">
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', width: '100%', marginBottom: '1rem', borderBottom: '1px solid #1f1f1f', paddingBottom: '1rem' }}>
                        {(event?.ticketTiers || []).map(t => (
                          <span key={t.id} style={{ display: 'flex', alignItems: 'center', color: '#f0f0f0' }}>
                            <span className="ptb-legend-dot" style={{ backgroundColor: tierColors[t.id.toLowerCase()] || '#888', marginRight: '6px' }} />
                            {t.name}
                          </span>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot available" /> Available</span>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot selected" /> Selected</span>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className="ptb-legend-dot taken" /> Taken</span>
                      </div>
                    </div>

                    <div style={{ padding: '1rem', backgroundColor: totalOrchestraSelected === complimentaryCount ? '#2d3748' : '#742a2a', borderRadius: '8px', marginBottom: '1rem', border: `1px solid ${totalOrchestraSelected === complimentaryCount ? '#68d391' : '#fc8181'}`, marginTop: '1rem' }}>
                      <h3 style={{ color: totalOrchestraSelected === complimentaryCount ? '#68d391' : '#fc8181', margin: 0 }}>
                        {totalOrchestraSelected} / {complimentaryCount} Seats Selected
                      </h3>
                      <p style={{ margin: '0.5rem 0 0 0', color: '#e2e8f0' }}>
                        {totalOrchestraSelected === complimentaryCount
                          ? "You have selected all your complimentary seats. You may continue."
                          : `Please select exactly ${complimentaryCount} seats to proceed.`}
                      </p>
                    </div>
                  </>
                )}
              </div>

              <div className="ptb-right-col">
                <div className="ptb-cart-summary">
                  {renderCartSummaryInner()}
                </div>
              </div>
            </div>

            <div className="ptb-actions">
              <button className="ptb-btn-ghost" onClick={goBack}>← Back</button>
              <button className="ptb-btn-primary" disabled={!canProceedStepOrchestra} onClick={goNext}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* STEP Details */}
        {step === logicalStepDetails && (
          <div className="ptb-card ptb-animate">
            <h2 className="ptb-card-title">Your Details</h2>
            <div className="ptb-form">
              {buyerType === 'winner' && (
                <>
                  <label className="ptb-label">Paying for Registrant (Winner)</label>
                  <input className="ptb-input" value={registrantName} readOnly style={{ backgroundColor: '#f5f5f5', color: '#666', cursor: 'not-allowed' }} />
                </>
              )}

              <label className="ptb-label">Buyer Name *</label>
              <input className="ptb-input" placeholder="e.g. Andi Santoso (Parent)" value={buyerName} onChange={e => setBuyerName(e.target.value)} />

              <label className="ptb-label">Buyer Email Address *</label>
              <input className="ptb-input" type="email" placeholder="email@example.com" value={userEmail} onChange={e => setUserEmail(e.target.value)} />

              <label className="ptb-label">Buyer Phone Number *</label>
              <input className="ptb-input" type="tel" placeholder="+62 812 3456 7890" value={userPhone} onChange={e => setUserPhone(e.target.value)} />

              <p className="ptb-hint" style={{ marginTop: '0.5rem' }}>
                Your booking confirmation and payment link will be sent to this email.
              </p>
            </div>

            <div className="ptb-actions">
              <button className="ptb-btn-ghost" onClick={goBack}>← Back</button>
              <button className="ptb-btn-primary" disabled={!canProceedStepDetails} onClick={goNext}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* STEP Review & Pay */}
        {step === logicalStepDetails + 1 && (
          <div className="ptb-card ptb-animate">
            <h2 className="ptb-card-title">Review & Pay</h2>

            <div className="ptb-summary">
              <div className="ptb-summary-row"><span>Venue</span><strong>{dynamicVenueInfo[venue]?.label || venue}</strong></div>
              <div className="ptb-summary-row"><span>Date</span><strong>{date}</strong></div>
              <div className="ptb-summary-row"><span>Session</span><strong>{session}</strong></div>
              <hr className="ptb-divider" />
              {buyerType === 'winner' ? (
                <>
                  <div className="ptb-summary-row"><span>Winner Profile</span><strong>{selectedWinner?.name} <span className={`ptb-award-inline ${selectedWinner?.finalAward?.toLowerCase()}`}>({selectedWinner?.finalAward})</span></strong></div>
                  <div className="ptb-summary-row"><span>Paying For (Registrant)</span><strong>{registrantName}</strong></div>
                </>
              ) : (
                <div className="ptb-summary-row"><span>Buyer Type</span><strong>Public Buyer</strong></div>
              )}
              <div className="ptb-summary-row"><span>Buyer Name</span><strong>{buyerName}</strong></div>
              <div className="ptb-summary-row"><span>Buyer Email</span><strong>{userEmail}</strong></div>
              <hr className="ptb-divider" />

              {complimentaryCount > 0 && (
                <div className="ptb-summary-row">
                  <span>{complimentaryCount}× Complimentary Ticket</span>
                  <strong>Free</strong>
                </div>
              )}

              {ticketsPayload.map(t => (
                <div key={t.id} className="ptb-summary-row">
                  <span>{t.quantity}× {t.name}</span>
                  <strong>{formatRupiah(t.quantity * t.priceEach)}</strong>
                </div>
              ))}

              {(() => {
                const uniqueAddOnIds = [...new Set(selectedAddOns)];
                return uniqueAddOnIds.map(addonId => {
                  const a = event?.addOns?.find(x => x.id === addonId);
                  if (!a) return null;
                  const qty = selectedAddOns.filter(id => id === addonId).length;
                  return (
                    <div key={a.id} className="ptb-summary-row">
                      <span>{qty}× {a.name}</span>
                      <strong>{formatRupiah(a.price * qty)}</strong>
                    </div>
                  );
                });
              })()}

              {freeMasterclassCount > 0 && (
                <div className="ptb-summary-row" style={{ color: '#EBBC64' }}>
                  <span>{freeMasterclassCount}× Master Class (Free with Presto)</span>
                  <strong>Free</strong>
                </div>
              )}

              {complimentaryCount > 0 && (
                <div className="ptb-summary-row ptb-seats-row" style={{ alignItems: 'flex-start' }}>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    Orchestra Seats
                    <span style={{ fontSize: '0.85em', color: '#999', fontWeight: 'normal' }}>
                      {(() => {
                        const os = allSessions.find(s => s.id === selectedSessionId);
                        if (os) {
                          return `${dynamicVenueInfo[os.venue]?.label || os.venue} - ${os.date} ${os.time}`;
                        }
                        return '';
                      })()}
                    </span>
                  </span>
                  <strong style={{ textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word', lineHeight: '1.4' }}>
                    {hasOrchestraStep && allOrchestraSelectedSeatIds.length > 0
                      ? Object.values(orchestraSelections).flat().map(s => s.tooltip?.split(' – ')[0]).join(', ')
                      : `${complimentaryCount} On-The-Spot Seats`}
                  </strong>
                </div>
              )}

              {allSelectedSeatIds.length > 0 && (
                <div className="ptb-summary-row ptb-seats-row" style={{ alignItems: 'flex-start' }}>
                  <span>
                    Seats
                  </span>
                  <strong style={{ textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word', lineHeight: '1.4' }}>
                    {Object.values(selections).flat().map(s => s.tooltip?.split(' – ')[0]).join(', ')}
                  </strong>
                </div>
              )}

              <hr className="ptb-divider" />
              <div className="ptb-summary-row ptb-total-row">
                <span>Total</span>
                <strong className="ptb-gold">{formatRupiah(orderTotal)}</strong>
              </div>
            </div>

            <div className="ptb-hold-notice">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EBBC64" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              <span>Your seats will be held for <strong>30 minutes</strong> after clicking Pay Now. Complete payment within this window to confirm your booking.</span>
            </div>

            {submitError && <p className="ptb-error-msg">{submitError}</p>}

            <div className="ptb-actions">
              <button className="ptb-btn-ghost" onClick={goBack} disabled={isSubmitting}>← Back</button>
              <button className="ptb-btn-primary ptb-pay-btn" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? <span className="ptb-spinner-sm" /> : null}
                {isSubmitting ? 'Processing…' : `Pay ${formatRupiah(orderTotal)}`}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
